"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.QuickOrderForm = void 0;
var _icons = require("@ori-ui/icons");
var _mui = require("@ori-ui/mui");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _downshift = _interopRequireDefault(require("downshift"));
var _hooks = require("../hooks");
var _logger = require("../logger");
var _AddButton = require("./AddButton");
var _QuantityPicker = require("./QuantityPicker");
var _QuickOrderFormResults = require("./QuickOrderFormResults");
var _jsxRuntime = require("react/jsx-runtime");
/* eslint-disable react/jsx-no-bind -- x */

const iconStyles = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2
};
const webkitAppearanceStyles = {
  margin: 0,
  WebkitAppearance: 'none'
};
const Root = (0, _mui.styled)('div')(({
  theme
}) => ({
  margin: '0 auto',
  padding: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3)
  }
}));
const Form = (0, _mui.styled)('form')(() => ({
  alignItems: 'center',
  display: 'flex',
  position: 'relative'
}));
const AutoComplete = (0, _mui.styled)('div')(({
  theme
}) => ({
  border: `2px solid ${theme.palette.grey[100]}`,
  borderRadius: '23px 0 0 23px',
  flexGrow: 1,
  overflow: 'hidden',
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('sm')]: {
    background: theme.palette.grey[100],
    border: `2px solid ${theme.palette.grey[100]}`,
    borderRadius: 23,
    flexGrow: 1,
    height: 36
  }
}));
const Input = (0, _mui.styled)('input')(({
  theme
}) => ({
  background: theme.palette.grey[100],
  border: 0,
  color: theme.palette.grey[900],
  fontSize: '1.6rem',
  height: 40,
  paddingLeft: theme.spacing(10),
  width: 'calc(100% - 50px)',
  '&::placeholder': {
    color: theme.palette.grey.A700
  },
  '&:focus': {
    outline: 'none'
  },
  '&::-webkit-outer-spin-button': webkitAppearanceStyles,
  '&::-webkit-inner-spin-button': webkitAppearanceStyles,
  MozAppearance: 'textfield',
  [theme.breakpoints.down('sm')]: {
    height: 34,
    paddingLeft: 42,
    width: 'calc(100% - 42px)'
  }
}));
const SearchIcon = (0, _mui.styled)('span')(({
  theme
}) => ({
  ...iconStyles,
  color: theme.palette.text.primary,
  fontSize: '2.4rem',
  left: theme.spacing(2)
}));
const ErrorIcon = (0, _mui.styled)('span')(({
  theme
}) => ({
  ...iconStyles,
  color: theme.palette.error.dark,
  fontSize: '3.2rem',
  right: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    right: 50
  }
}));
const LoadingIcon = (0, _mui.styled)(_mui.CircularProgress)(({
  theme
}) => ({
  height: '32px !important',
  margin: '0 59px',
  width: '32px !important',
  zIndex: 2,
  '& svg': {
    color: theme.palette.text.primary,
    opacity: 0.1
  },
  [theme.breakpoints.down('sm')]: {
    height: '24px !important',
    margin: 0,
    position: 'absolute',
    right: theme.spacing(2),
    width: '24px !important'
  }
}));
const QuickOrderForm = () => {
  const {
    error,
    handleButtonClick,
    handleChange,
    handleFocus,
    handleKeyDown,
    handleQuantityBlur,
    input,
    isFocused,
    isLoading,
    isWhispererLoading,
    itemToString,
    maxValue,
    minValue,
    onStateChange,
    quantity,
    results,
    setFocus,
    setQuantity,
    stateReducer,
    value,
    whispererError
  } = (0, _hooks.useQuickOrderForm)();
  const {
    getTestId
  } = (0, _testidGenerator.default)();
  const {
    formatMessage
  } = (0, _hooks.useTranslations)();
  (0, _logger.logDebug)('isLoading', isLoading);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(Root, {
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_downshift.default, {
      itemToString: itemToString,
      stateReducer: stateReducer,
      onStateChange: onStateChange,
      onChange: handleChange,
      children: ({
        clearSelection,
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        highlightedIndex,
        isOpen
      }) => /*#__PURE__*/(0, _jsxRuntime.jsxs)(Form, {
        "data-testid": getTestId('form'),
        ...getRootProps(),
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(AutoComplete, {
          onClick: setFocus,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(SearchIcon, {
            "data-testid": getTestId('search-icon'),
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.Search, {
              fontSize: "inherit"
            })
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Input, {
            ref: input
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any -- x
            ,

            as: undefined,
            "data-testid": getTestId('search-input'),
            name: "suggestions",
            pattern: "[0-9]*",
            placeholder: formatMessage('placeholder'),
            type: "number",
            tabIndex: 0,
            ...getInputProps({
              onFocus: handleFocus,
              onBlur: handleFocus,
              onKeyDown: handleKeyDown
            })
          }), (Boolean(error) || Boolean(whispererError)) && /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Tooltip, {
            title: whispererError,
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)(ErrorIcon, {
              "data-testid": getTestId('error-icon'),
              children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.ErrorCircle, {
                fontSize: "inherit"
              })
            })
          })]
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_QuantityPicker.QuantityPicker, {
          "data-testid": getTestId('quantity'),
          maxQty: maxValue,
          minQty: minValue,
          quantityLabel: formatMessage('qty'),
          setQuantity: setQuantity,
          tabIndex: 0,
          value: quantity,
          onBlur: handleQuantityBlur,
          onFocus: handleFocus
        }), isLoading || isWhispererLoading ? /*#__PURE__*/(0, _jsxRuntime.jsx)(LoadingIcon, {
          "data-testid": getTestId('loading-icon'),
          thickness: 5
        }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_AddButton.AddButton, {
          "data-testid": getTestId('add-button'),
          isFocused: isFocused,
          hasValue: value !== '',
          onBlur: handleFocus,
          onClick: () => handleButtonClick(clearSelection),
          onFocus: handleFocus
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("ul", {
          ...getMenuProps()
        }), isOpen && value !== '' ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_QuickOrderFormResults.QuickOrderFormResults, {
          "data-testid": getTestId('results'),
          inputValue: value,
          isLoading: isLoading || isWhispererLoading,
          results: results,
          getItemProps,
          getMenuProps,
          highlightedIndex
        }) : null]
      })
    })
  });
};
exports.QuickOrderForm = QuickOrderForm;